import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';

const QADataQualityPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDebug, setShowDebug] = useState(false); // Debug toggle
  
  // Case selection
  const [caseSearch, setCaseSearch] = useState('');
  const [caseSearchResults, setCaseSearchResults] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isSearchingCases, setIsSearchingCases] = useState(false);
  
  // QA Data
  const [qaData, setQaData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Protocol selection
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  
  // Filters
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAllDoctors, setSelectAllDoctors] = useState(true);
  const [selectAllQuestions, setSelectAllQuestions] = useState(true);
  
  // Debounce search
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (caseSearch.length >= 1) {
        searchCases(caseSearch);
      } else {
        setCaseSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(delaySearch);
  }, [caseSearch]);
  
  // Initialize question selection when data loads
  useEffect(() => {
    if (qaData && qaData.questions && qaData.questions.length > 0) {
      // Filter questions by selected protocol if available
      let filteredQuestions = qaData.questions;
      if (selectedProtocol) {
        filteredQuestions = qaData.questions.filter(q => q.protocol_id === selectedProtocol.id);
      }
      
      if (selectedQuestions.length === 0 && filteredQuestions.length > 0) {
        setSelectedQuestions(filteredQuestions.map(q => q.id));
      }
    }
  }, [qaData, selectedProtocol]);
  
  // Initialize doctor selection when data loads
  useEffect(() => {
    if (qaData && qaData.doctors && qaData.doctors.length > 0) {
      if (selectedDoctors.length === 0) {
        setSelectedDoctors(qaData.doctors.map(d => d.id));
      }
    }
  }, [qaData]);
  
  const searchCases = async (searchTerm) => {
    setIsSearchingCases(true);
    try {
      const response = await api.get(`/answers/qa/search-cases?search=${encodeURIComponent(searchTerm)}`);
      if (response.data.success) {
        setCaseSearchResults(response.data.cases || []);
      } else {
        setCaseSearchResults([]);
      }
    } catch (err) {
      console.error('Failed to search cases:', err);
      setCaseSearchResults([]);
    } finally {
      setIsSearchingCases(false);
    }
  };
  
  const selectCase = async (caseItem) => {
    setSelectedCase(caseItem);
    setCaseSearch('');
    setCaseSearchResults([]);
    await loadCaseData(caseItem.id);
  };
  
  const loadCaseData = async (caseId) => {
    setIsLoadingData(true);
    setError(null);
    try {
      const response = await api.get(`/answers/qa/case-data?case_id=${caseId}`);
      if (response.data.success) {
        setQaData(response.data.data);
        
        // Set protocols from response
        const protocolsList = response.data.data.protocols || [];
        setProtocols(protocolsList);
        
        // Set selected protocol (default to first one or the one in selected_protocol_id)
        let defaultProtocol = null;
        if (protocolsList.length > 0) {
          defaultProtocol = response.data.data.selected_protocol_id 
            ? protocolsList.find(p => p.id === response.data.data.selected_protocol_id)
            : protocolsList[0];
        }
        setSelectedProtocol(defaultProtocol);
        
        // Reset filters - but filter questions by selected protocol first
        const allDoctors = response.data.data.doctors || [];
        const allQuestions = response.data.data.questions || [];
        
        setSelectedDoctors(allDoctors.map(d => d.id));
        
        // Filter questions by the default protocol
        if (defaultProtocol) {
          const protocolQuestions = allQuestions.filter(q => q.protocol_id === defaultProtocol.id);
          setSelectedQuestions(protocolQuestions.map(q => q.id));
        } else {
          setSelectedQuestions(allQuestions.map(q => q.id));
        }
        
        setSelectAllDoctors(true);
        setSelectAllQuestions(true);
      } else {
        setError('Failed to load case data');
      }
    } catch (err) {
      console.error('Failed to load case data:', err);
      setError('Failed to load case data: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoadingData(false);
    }
  };
  
  const clearCase = () => {
    setSelectedCase(null);
    setQaData(null);
    setProtocols([]);
    setSelectedProtocol(null);
    setSelectedDoctors([]);
    setSelectedQuestions([]);
    setSelectAllDoctors(true);
    setSelectAllQuestions(true);
  };
  
  const toggleDoctor = (doctorId) => {
    if (selectAllDoctors) {
      setSelectAllDoctors(false);
      setSelectedDoctors([doctorId]);
    } else {
      if (selectedDoctors.includes(doctorId)) {
        setSelectedDoctors(selectedDoctors.filter(id => id !== doctorId));
      } else {
        setSelectedDoctors([...selectedDoctors, doctorId]);
      }
    }
  };
  
  const toggleQuestion = (questionId) => {
    if (selectAllQuestions) {
      setSelectAllQuestions(false);
      setSelectedQuestions([questionId]);
    } else {
      if (selectedQuestions.includes(questionId)) {
        setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
      } else {
        setSelectedQuestions([...selectedQuestions, questionId]);
      }
    }
  };
  
  const selectAllDoctorsToggle = () => {
    if (selectAllDoctors) {
      setSelectAllDoctors(false);
      setSelectedDoctors([]);
    } else {
      setSelectAllDoctors(true);
      if (qaData && qaData.doctors) {
        setSelectedDoctors(qaData.doctors.map(d => d.id));
      }
    }
  };
  
  const selectAllQuestionsToggle = () => {
    if (selectAllQuestions) {
      setSelectAllQuestions(false);
      setSelectedQuestions([]);
    } else {
      setSelectAllQuestions(true);
      if (qaData && qaData.questions) {
        setSelectedQuestions(qaData.questions.map(q => q.id));
      }
    }
  };
  
  const getFilteredDoctors = () => {
    if (!qaData || !qaData.doctors) return [];
    if (selectAllDoctors) return qaData.doctors;
    return qaData.doctors.filter(d => selectedDoctors.includes(d.id));
  };
  
  const getFilteredQuestions = () => {
    if (!qaData || !qaData.questions) return [];
    
    // First filter by protocol if one is selected
    let questions = qaData.questions;
    if (selectedProtocol) {
      questions = questions.filter(q => q.protocol_id === selectedProtocol.id);
    }
    
    // Then filter by selected questions
    if (selectAllQuestions) return questions;
    return questions.filter(q => selectedQuestions.includes(q.id));
  };
  
  const getDoctorAnswer = (doctor, questionId) => {
    if (!doctor || !doctor.answers) {
      return null;
    }
    
    // Use the question ID directly as the key (backend returns keys as question IDs)
    const answerKey = String(questionId);
    
    return doctor.answers[answerKey] || null;
  };
  
  const getQuestionShortText = (question) => {
    // Get full question text
    if (!question || !question.text) return 'Q' + (question?.id || '?');
    return question.text;
  };
  
  const getPredictionColor = (prediction) => {
    if (!prediction) return '#000';
    const lower = prediction.toLowerCase();
    if (lower.includes('red')) return '#ff6666';
    if (lower.includes('orange')) return '#ffa500';
    if (lower.includes('yellow')) return '#ffe366';
    if (lower.includes('green')) return '#90ee90';
    return '#000';
  };
  
  // Render filter section
  const renderFilters = () => (
    <div className="row mb-4">
      <div className="col-md-6">
        <div className="card">
          <div className="card-header bg-warning text-dark">
            <h6 className="mb-0"><i className="fas fa-user-md me-2"></i>Filter Doctor(s) per Case</h6>
          </div>
          <div className="card-body">
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="selectAllDoctorsCheck"
                checked={selectAllDoctors}
                onChange={selectAllDoctorsToggle}
              />
              <label className="form-check-label" htmlFor="selectAllDoctorsCheck">
                All Doctors
              </label>
            </div>
            <div className="doctor-filter-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {qaData && qaData.doctors && qaData.doctors.map(doctor => (
                <div key={doctor.id} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`doctor-${doctor.id}`}
                    checked={selectAllDoctors || selectedDoctors.includes(doctor.id)}
                    onChange={() => toggleDoctor(doctor.id)}
                    disabled={selectAllDoctors}
                  />
                  <label className="form-check-label" htmlFor={`doctor-${doctor.id}`}>
                    {doctor.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="card">
          <div className="card-header bg-warning text-dark">
            <h6 className="mb-0">
              <i className="fas fa-question-circle me-2"></i>
              Filter by Questions
              {selectedProtocol && <span className="ms-2 badge bg-info">{selectedProtocol.name}</span>}
            </h6>
          </div>
          <div className="card-body">
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="selectAllQuestionsCheck"
                checked={selectAllQuestions}
                onChange={selectAllQuestionsToggle}
              />
              <label className="form-check-label" htmlFor="selectAllQuestionsCheck">
                All Questions
              </label>
            </div>
            <div className="question-filter-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {(selectedProtocol 
                ? qaData.questions.filter(q => q.protocol_id === selectedProtocol.id)
                : qaData.questions
              ).map((question, index) => (
                <div key={question.id} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`question-${question.id}`}
                    checked={selectAllQuestions || selectedQuestions.includes(question.id)}
                    onChange={() => toggleQuestion(question.id)}
                    disabled={selectAllQuestions}
                  />
                  <label className="form-check-label" htmlFor={`question-${question.id}`}>
                    <strong>Q{index + 1}:</strong> {getQuestionShortText(question)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Render data table
  const renderDataTable = () => {
    const filteredDoctors = getFilteredDoctors();
    const filteredQuestions = getFilteredQuestions();
    
    if (!qaData || filteredDoctors.length === 0 || filteredQuestions.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-table fa-3x mb-3"></i>
          <p>Select a case and apply filters to view data</p>
        </div>
      );
    }
    
    return (
      <div className="table-responsive">
        <table className="table table-bordered" style={{ backgroundColor: '#fff' }}>
          <thead style={{ backgroundColor: '#FFA500', color: '#000' }}>
            <tr>
              <th style={{ minWidth: '150px', backgroundColor: '#FFA500' }}>Doctor</th>
              {filteredQuestions.map((question, index) => (
                <th key={question.id} style={{ minWidth: '300px', backgroundColor: '#FFA500' }}>
                  <div>Q{index + 1} (ID: {question.id})</div>
                  <small style={{ fontSize: '0.7em' }}>{getQuestionShortText(question)}</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredDoctors.map(doctor => (
              <tr key={doctor.id}>
                <td style={{ backgroundColor: '#FFA500', fontWeight: 'bold' }}>
                  {doctor.name}
                </td>
                {filteredQuestions.map(question => {
                  // Use the question ID directly as the key (backend returns keys as question IDs)
                  const answerKey = String(question.id);
                  
                  // Get answer using the correct key
                  const doctorAnswers = doctor.answers || {};
                  const answer = doctorAnswers[answerKey] || null;
                  
                  return (
                    <td key={question.id} style={{ backgroundColor: '#FFA500', minWidth: '300px' }}>
                      <div style={{ 
                        backgroundColor: '#fff', 
                        padding: '8px', 
                        borderRadius: '4px',
                        minHeight: '60px',
                        fontSize: '0.9em'
                      }}>
                        {answer ? (
                          <span style={{ color: 'green' }}>{String(answer).substring(0, 100)}</span>
                        ) : (
                          <span className="text-muted">No answer</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  return (
    <ResearchLayout pageTitle="Tabular QA Interface">
      <div className="container-fluid">
        {/* Success/Error Messages */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
          </div>
        )}
        
        {/* Case Selection Section */}
        <div className="card mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-search me-2"></i>Case Selection
            </h5>
          </div>
          <div className="card-body">
            {!selectedCase ? (
              <div className="position-relative">
                <label htmlFor="caseSearch" className="form-label">Search Case Number</label>
                <input
                  type="text"
                  className="form-control"
                  id="caseSearch"
                  placeholder="Enter case ID or customer ID..."
                  value={caseSearch}
                  onChange={(e) => setCaseSearch(e.target.value)}
                  autoComplete="off"
                />
                {caseSearchResults.length > 0 && (
                  <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                    {caseSearchResults.map(caseItem => (
                      <div
                        key={caseItem.id}
                        className="p-2 border-bottom hover-bg-light cursor-pointer"
                        onClick={() => selectCase(caseItem)}
                        style={{ cursor: 'pointer' }}
                      >
                        <strong>Case {caseItem.id}</strong>
                        <span className="text-muted ms-2">({caseItem.doctors_count} doctors)</span>
                      </div>
                    ))}
                  </div>
                )}
                {isSearchingCases && (
                  <div className="text-muted mt-2">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Searching...
                  </div>
                )}
              </div>
            ) : (
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="badge bg-primary me-2">Case {selectedCase.id}</span>
                </div>
                <button className="btn btn-outline-secondary" onClick={clearCase}>
                  <i className="fas fa-times me-1"></i>Clear Case
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Selected Case Info */}
        {selectedCase && (
          <div className="alert alert-info mb-4">
            <strong>Selected Case:</strong> Case {selectedCase.id}
          </div>
        )}
        
        {/* QA Data Section */}
        {selectedCase && (
          <>
            {/* Case Details */}
            {qaData && qaData.case && (
              <div className="card mb-4">
                <div className="card-header bg-info text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Case {qaData.case.id} Details
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <strong>Score:</strong> {qaData.case.score || 'N/A'}/100
                    </div>
                    <div className="col-md-3">
                      <strong>Completion:</strong> {qaData.case.completionPercentage || 0}%
                    </div>
                    <div className="col-md-3">
                      <strong>Age:</strong> {qaData.case.age ? `${qaData.case.age}y` : 'N/A'}
                    </div>
                    <div className="col-md-3">
                      <strong>Gender:</strong> {qaData.case.gender === 0 ? 'Male' : qaData.case.gender === 1 ? 'Female' : 'N/A'}
                    </div>
                  </div>
                  
                  <div className="row">
                    {/* Physical Measurements */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Physical</strong>
                        <small>
                          Weight: {qaData.case.weight ? `${qaData.case.weight} ${qaData.case.weightUnit || 'kg'}` : 'N/A'}<br/>
                          Height: {qaData.case.height ? `${qaData.case.height} ${qaData.case.heightUnit || 'cm'}` : 'N/A'}<br/>
                          Waist: {qaData.case.waist ? `${qaData.case.waist} ${qaData.case.waistUnit || 'cm'}` : 'N/A'}<br/>
                          Hip: {qaData.case.hip ? `${qaData.case.hip} ${qaData.case.hipUnit || 'cm'}` : 'N/A'}<br/>
                          BMI: {qaData.case.bmi || 'N/A'}
                        </small>
                      </div>
                    </div>
                    
                    {/* Vitals */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Vitals</strong>
                        <small>
                          Systolic BP: {qaData.case.systolicBloodPressure || 'N/A'}<br/>
                          Diastolic BP: {qaData.case.diastolicBloodPressure || 'N/A'}<br/>
                          Heart Rate: {qaData.case.restingHeartRate || 'N/A'} bpm<br/>
                          Blood Glucose: {qaData.case.bloodGlucose ? `${qaData.case.bloodGlucose} ${qaData.case.bloodGlucoseUnit || 'mg/dL'}` : 'N/A'}<br/>
                          O2 Sat: {qaData.case.oxygenSaturationLevel || 'N/A'}%
                        </small>
                      </div>
                    </div>
                    
                    {/* Lifestyle */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Lifestyle</strong>
                        <small>
                          Smoking: {qaData.case.isSmoker ? `Yes, ${qaData.case.cigarettesPerDay || 0}/day` : 'No'}<br/>
                          Alcohol: {qaData.case.isDrinker ? 'Yes' : 'No'}<br/>
                          Activity: {qaData.case.activityLevel || 'N/A'}<br/>
                          Sleep: {qaData.case.sleepLevel || 'N/A'}
                        </small>
                      </div>
                    </div>
                    
                    {/* Symptoms */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Symptoms</strong>
                        <small>
                          Pain: {qaData.case.painLevel || '0'}<br/>
                          Vision: {qaData.case.visionLevel || '0'}<br/>
                          Hearing: {qaData.case.hearingLevel || '0'}
                        </small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="row mt-2">
                    {/* Medical */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Medical</strong>
                        <small>
                          Chronic Disease: {qaData.case.chronicDisease || 'N/A'}<br/>
                          Family History: {qaData.case.familyHistory || 'N/A'}<br/>
                          Education: {qaData.case.education || 'N/A'}
                        </small>
                      </div>
                    </div>
                    
                    {/* Mental */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Mental</strong>
                        <small>
                          Stress: {qaData.case.stressFrequency || 'N/A'}<br/>
                          Mood: {qaData.case.mood || 'N/A'}<br/>
                          Appetite: {qaData.case.appetiteFrequency || 'N/A'}<br/>
                          Fatigue: {qaData.case.fatigueFrequency || 'N/A'}
                        </small>
                      </div>
                    </div>
                    
                    {/* Mental Health Color */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">Mental Health</strong>
                        <small>
                          <span style={{ 
                            display: 'inline-block',
                            width: '24px', 
                            height: '24px', 
                            backgroundColor: qaData.case.mentalHealthColor || '#ccc',
                            borderRadius: '4px',
                            verticalAlign: 'middle'
                          }}></span>
                          <span className="ms-2">{qaData.case.mentalHealthColor || 'N/A'}</span>
                        </small>
                      </div>
                    </div>
                    
                    {/* AI Predictions */}
                    <div className="col-md-3 mb-2">
                      <div className="border rounded p-2 bg-light">
                        <strong className="d-block mb-1">AI Assessment</strong>
                        <small>
                          Physical: <span style={{ color: getPredictionColor(qaData.case.physicalHealthPrediction) }}>{qaData.case.physicalHealthPrediction || 'N/A'}</span><br/>
                          Clinical: <span style={{ color: getPredictionColor(qaData.case.clinicalHealthPrediction) }}>{qaData.case.clinicalHealthPrediction || 'N/A'}</span><br/>
                          Lifestyle: <span style={{ color: getPredictionColor(qaData.case.lifestylePrediction) }}>{qaData.case.lifestylePrediction || 'N/A'}</span>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Protocol Selection - After Case Details */}
            {qaData && protocols.length > 0 && (
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-list-alt me-2"></i>
                    Protocol Selection
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <label htmlFor="protocolSelect" className="form-label">
                        <strong>Select Protocol:</strong>
                      </label>
                      <select
                        className="form-select"
                        id="protocolSelect"
                        value={selectedProtocol?.id || ''}
                        onChange={(e) => {
                          const protocolId = parseInt(e.target.value);
                          const protocol = protocols.find(p => p.id === protocolId);
                          setSelectedProtocol(protocol);
                          // Reset question selection when protocol changes
                          const protocolQuestions = qaData.questions.filter(q => q.protocol_id === protocolId);
                          setSelectedQuestions(protocolQuestions.map(q => q.id));
                          setSelectAllQuestions(true);
                        }}
                      >
                        <option value="">-- Select Protocol --</option>
                        {protocols.map(protocol => (
                          <option key={protocol.id} value={protocol.id}>
                            {protocol.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <div className="alert alert-info mb-0">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          {selectedProtocol 
                            ? `Showing questions for: ${selectedProtocol.name}`
                            : 'Select a protocol to view its questions'
                          }
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Loading State */}
            {isLoadingData && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading case data...</p>
              </div>
            )}
            
            {/* Filters */}
            {!isLoadingData && qaData && (
              <>
                {renderFilters()}
                
                {/* Data Table */}
                {renderDataTable()}
                
                {/* Summary */}
                <div className="mt-3 text-muted">
                  <small>
                    Showing {getFilteredDoctors().length} of {qaData.doctors?.length || 0} doctors
                    and {getFilteredQuestions().length} of {selectedProtocol 
                      ? qaData.questions?.filter(q => q.protocol_id === selectedProtocol.id).length || 0
                      : qaData.questions?.length || 0} questions
                    {selectedProtocol && <span className="ms-1">(filtered by {selectedProtocol.name})</span>}
                  </small>
                </div>
              </>
            )}
          </>
        )}
        
        {/* Debug Toggle  */}
        {/* <div className="mb-3">
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowDebug(!showDebug)}
          >
            <i className="fas fa-bug me-1"></i>
            {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
          </button>
        </div> */}
        
        {/* Debug View */}
        {showDebug && qaData && (
          <div className="card mb-4">
            <div className="card-header bg-dark text-white">
              <h6 className="mb-0"><i className="fas fa-code me-2"></i>Debug Information</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6>Doctors ({qaData.doctors?.length || 0})</h6>
                  <pre style={{ fontSize: '0.75em', maxHeight: '300px', overflow: 'auto' }}>
                    {JSON.stringify(qaData.doctors, null, 2)}
                  </pre>
                  {/* Direct verification of first doctor's answers */}
                  {qaData.doctors?.[0] && (
                    <div className="mt-2 p-2 bg-light border">
                      <strong>First Doctor's Answers Structure:</strong>
                      <div>Doctor ID: {qaData.doctors[0].id}</div>
                      <div>Doctor Name: {qaData.doctors[0].name}</div>
                      <div>Answers type: {typeof qaData.doctors[0].answers}</div>
                      <div>Answers keys: {JSON.stringify(Object.keys(qaData.doctors[0].answers || {}))}</div>
                      <div>Answers values preview: {JSON.stringify(qaData.doctors[0].answers || {})}</div>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <h6>Questions ({qaData.questions?.length || 0})</h6>
                  <pre style={{ fontSize: '0.75em', maxHeight: '300px', overflow: 'auto' }}>
                    {JSON.stringify(qaData.questions, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* No Case Selected */}
        {!selectedCase && (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-clipboard-check fa-4x mb-3"></i>
            <h5>Patient Data Quality Assurance Interface</h5>
            <p>Search and select a case to begin quality assurance review</p>
          </div>
        )}
      </div>
      
      {/* Custom Styles */}
      <style jsx>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        .cursor-pointer {
          cursor: pointer !important;
        }
      `}</style>
    </ResearchLayout>
  );
};

export default QADataQualityPage;
